import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { AuthService } from '../../../core/auth/auth.service';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { CommissionRecoveryExportService } from '../services/commission-recovery-export.service';
import {
  CommissionRecoveryApplicationItem,
  CommissionRecoveryApplicationStatus,
  CommissionRecoveryItem,
  CommissionRecoveryService,
  CommissionRecoveryStatus,
} from '../services/commission-recovery.service';

type RecoveryRow = Omit<
  CommissionRecoveryItem,
  'originalAmount' | 'recoveryAmount' | 'settledAmount' | 'remainingAmount' | 'bookingRefund' | 'bookingCommission' | 'applications'
> & {
  originalAmount: number;
  recoveryAmount: number;
  settledAmount: number;
  remainingAmount: number;
  bookingRefund: Omit<CommissionRecoveryItem['bookingRefund'], 'amount'> & { amount: number };
  bookingCommission: Omit<CommissionRecoveryItem['bookingCommission'], 'totalAmount'> & {
    totalAmount: number;
  };
  applications: Array<Omit<CommissionRecoveryApplicationItem, 'amount'> & { amount: number }>;
};

@Component({
  selector: 'app-commission-recoveries-page',
  imports: [
    DatePipe,
    DatePickerModule,
    DecimalPipe,
    FormsModule,
    InputTextModule,
    LucideAngularModule,
    RouterLink,
    SelectModule,
    StatusBadge,
    TableModule,
  ],
  templateUrl: './commission-recoveries-page.component.html',
})
export class CommissionRecoveriesPageComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly overlay = inject(GlobalOverlayService);
  private readonly exportService = inject(CommissionRecoveryExportService);
  private readonly service = inject(CommissionRecoveryService);

  readonly rows = signal<RecoveryRow[]>([]);
  readonly isLoading = signal(false);
  readonly searchTerm = signal('');
  readonly selectedStatus = signal<'ALL' | CommissionRecoveryStatus>('ALL');
  readonly dateFrom = signal<Date | null>(null);
  readonly dateTo = signal<Date | null>(null);
  readonly pendingStatus = signal<'ALL' | CommissionRecoveryStatus>('ALL');
  readonly pendingDateFrom = signal<Date | null>(null);
  readonly pendingDateTo = signal<Date | null>(null);
  readonly selectedRecovery = signal<RecoveryRow | null>(null);
  readonly canExportExcel = computed(() => {
    const user = this.auth.user();
    return user?.role.level === 1 || user?.department.code === 'ACC';
  });

  readonly statusOptions: Array<{ label: string; value: 'ALL' | CommissionRecoveryStatus }> = [
    { label: 'ทุกสถานะ', value: 'ALL' },
    { label: 'รอหักคืน', value: 'PENDING' },
    { label: 'หักคืนบางส่วน', value: 'PARTIAL' },
    { label: 'หักคืนครบแล้ว', value: 'SETTLED' },
    { label: 'ยกเว้นยอด', value: 'WAIVED' },
    { label: 'ยกเลิก', value: 'CANCELLED' },
  ];

  readonly filteredRows = computed(() => {
    const query = this.searchTerm().trim().toLocaleLowerCase('th');
    const status = this.selectedStatus();
    const dateFrom = this.dateFrom();
    const dateTo = this.dateTo();
    const fromTimestamp = dateFrom
      ? new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate()).getTime()
      : null;
    const toTimestamp = dateTo
      ? new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate(), 23, 59, 59, 999).getTime()
      : null;

    return this.rows().filter((row) => {
      if (status !== 'ALL' && row.status !== status) return false;

      const createdAt = new Date(row.createdAt).getTime();
      if (fromTimestamp !== null && createdAt < fromTimestamp) return false;
      if (toTimestamp !== null && createdAt > toTimestamp) return false;

      if (!query) return true;
      return [
        row.recipientUser.employeeCode,
        row.recipientUser.firstName,
        row.recipientUser.lastName,
        row.booking.bookingNo,
        row.booking.tourName,
        row.bookingCommission.commissionNo,
        row.bookingRefund.refundNo,
        row.status,
      ]
        .join(' ')
        .toLocaleLowerCase('th')
        .includes(query);
    });
  });

  ngOnInit(): void {
    void this.loadRecoveries();
  }

  applyFilters(): void {
    this.dateFrom.set(this.pendingDateFrom());
    this.dateTo.set(this.pendingDateTo());
    this.selectedStatus.set(this.pendingStatus());
  }

  resetFilters(): void {
    this.pendingDateFrom.set(null);
    this.pendingDateTo.set(null);
    this.pendingStatus.set('ALL');
    this.applyFilters();
  }

  async exportExcel(): Promise<void> {
    if (!this.canExportExcel()) return;

    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.overlay.error('ไม่สามารถ Export ได้', 'ไม่พบยอดหักคืนค่าคอมตามตัวกรองปัจจุบัน');
      return;
    }

    this.overlay.showLoading('กำลังสร้างไฟล์ Excel...');
    try {
      await this.exportService.export(rows, {
        dateFrom: this.formatDateFilter(this.dateFrom()),
        dateTo: this.formatDateFilter(this.dateTo()),
        status:
          this.statusOptions.find((option) => option.value === this.selectedStatus())?.label ??
          'ทุกสถานะ',
        searchTerm: this.searchTerm().trim(),
      });
    } catch {
      this.overlay.error('สร้างไฟล์ Excel ไม่สำเร็จ', 'กรุณาลองใหม่อีกครั้ง');
    } finally {
      this.overlay.hideLoading();
    }
  }

  async loadRecoveries(): Promise<void> {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    try {
      const recoveries = await this.service.getAll();
      this.rows.set(
        recoveries.map((recovery) => ({
          ...recovery,
          originalAmount: Number(recovery.originalAmount),
          recoveryAmount: Number(recovery.recoveryAmount),
          settledAmount: Number(recovery.settledAmount),
          remainingAmount: Number(recovery.remainingAmount),
          bookingRefund: { ...recovery.bookingRefund, amount: Number(recovery.bookingRefund.amount) },
          bookingCommission: {
            ...recovery.bookingCommission,
            totalAmount: Number(recovery.bookingCommission.totalAmount),
          },
          applications: recovery.applications.map((application) => ({
            ...application,
            amount: Number(application.amount),
          })),
        })),
      );
    } catch (error: unknown) {
      this.overlay.error('โหลดยอดหักคืนค่าคอมไม่สำเร็จ', this.errorMessage(error));
    } finally {
      this.isLoading.set(false);
    }
  }

  openHistory(row: RecoveryRow): void {
    this.selectedRecovery.set(row);
  }

  closeHistory(): void {
    this.selectedRecovery.set(null);
  }

  recoveryStatusLabel(status: CommissionRecoveryStatus): string {
    return {
      PENDING: 'รอหักคืน',
      PARTIAL: 'หักคืนบางส่วน',
      SETTLED: 'หักคืนครบแล้ว',
      WAIVED: 'ยกเว้นยอด',
      CANCELLED: 'ยกเลิก',
    }[status];
  }

  recoveryStatusTone(
    status: CommissionRecoveryStatus,
  ): 'success' | 'danger' | 'warning' | 'neutral' {
    return {
      PENDING: 'warning',
      PARTIAL: 'warning',
      SETTLED: 'success',
      WAIVED: 'neutral',
      CANCELLED: 'danger',
    }[status] as 'success' | 'danger' | 'warning' | 'neutral';
  }

  recoveryStatusClass(status: CommissionRecoveryStatus): string {
    return {
      PENDING: 'bg-amber-50 text-amber-700 ring-amber-200',
      PARTIAL: 'bg-amber-50 text-amber-700 ring-amber-200',
      SETTLED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
      WAIVED: 'bg-slate-100 text-slate-700 ring-slate-200',
      CANCELLED: 'bg-red-50 text-red-700 ring-red-200',
    }[status];
  }

  applicationStatusLabel(status: CommissionRecoveryApplicationStatus): string {
    return {
      RESERVED: 'กันยอดไว้',
      APPLIED: 'หักคืนแล้ว',
      RELEASED: 'คืนยอดกลับ',
    }[status];
  }

  applicationTargetLabel(application: CommissionRecoveryApplicationItem): string {
    return application.targetType === 'TOUR_COMMISSION' ? 'ค่าคอม Tour' : 'ค่าคอม Booking';
  }

  applicationTargetCode(application: CommissionRecoveryApplicationItem): string {
    return application.bookingCommission?.commissionNo ?? application.tourCommission?.commissionNo ?? '-';
  }

  applicationReference(application: CommissionRecoveryApplicationItem): string {
    return (
      application.bookingCommission?.booking.bookingNo ??
      application.tourCommission?.tour.tourNo ??
      '-'
    );
  }

  private errorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse && typeof error.error?.message === 'string') {
      return error.error.message;
    }
    return 'กรุณาลองใหม่อีกครั้ง';
  }

  private formatDateFilter(date: Date | null): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
